import { Box, Tooltip, Typography } from '@mui/material';

import { CHAPITRE_COLORS } from './chapitreColors';

const GREY = '#555555';

export interface Segment {
  /** largeur relative (points) */
  points: number;
  /** part du segment acquise : 0 ou 1 pour une question, une fraction pour une moyenne de chapitre */
  ratio: number;
  chapitre: string;
  /** chapitre non sélectionné : segment très atténué, non compté */
  off?: boolean;
  tooltip: string;
}

interface Props {
  title: string;
  subtitle?: string;
  segments: Segment[];
  /** total du barème représenté (20 pour un sujet) */
  total: number;
  got: number;
  totalSel: number;
}

const fmt = (x: number) => x.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

/** Barre segmentée : un segment par question (ou par chapitre pour une moyenne), largeur ∝ points. */
export default function SegmentBar({ title, subtitle, segments, total, got, totalSel }: Props) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
        <Box>
          <Typography fontWeight={600}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap', ml: 1 }}>
          {fmt(got)} / {fmt(totalSel)} pts
        </Typography>
      </Box>
      <Box
        role="img"
        aria-label={`${title} : ${fmt(got)} points sur ${fmt(totalSel)}`}
        sx={{ display: 'flex', height: 22, width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: '#1a1a1a' }}
      >
        {segments.map((s, i) => {
          const color = CHAPITRE_COLORS[s.chapitre] || GREY;
          const w = total ? (100 * s.points) / total : 0;
          return (
            <Tooltip key={i} title={s.tooltip} enterTouchDelay={0} arrow>
              <Box
                sx={{
                  width: `${w}%`,
                  height: '100%',
                  boxSizing: 'border-box',
                  borderRight: i < segments.length - 1 ? '2px solid #2A2A2A' : 'none',
                  bgcolor: s.off ? 'transparent' : GREY,
                  opacity: s.off ? 0.35 : 1,
                  position: 'relative',
                  cursor: 'default',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: `${100 * Math.max(0, Math.min(1, s.ratio))}%`,
                    bgcolor: s.off ? '#3a3a3a' : color,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
